// Hub-and-spoke network diagram (artboard C): inflow → stock → outflow, left
// to right. Hub ring color = weeks of supply vs the hub's target; blue edges
// = in-transit volume (stroke ∝ units), green bands = weekly outflow to
// sales (stroke ∝ units/wk). Every hub-role location renders as its own
// circle; spoke lanes fan out from the primary (largest) hub.
//
// Rendering rules this file must keep (SVG paints in document order):
//   1. All edges are emitted first as one layer, all labels second — a later
//      thick band must never cover an earlier label.
//   2. Every edge label lives in the haloed <g paintOrder="stroke"> group so
//      small captions stay legible wherever a band passes beneath them.
import {
  SnapshotChannel,
  SnapshotInbound,
  SnapshotLocation,
  SnapshotPeriod,
  SnapshotSales,
  SnapshotTransferLane,
  SUPPLY_UNIT_LABEL,
} from "./types";
import {
  SALES_NODE_WIDTH,
  SalesNode,
  salesNodeHeight,
} from "./sales_node";
import { EM_DASH, un, usd, useSnapshotTokens } from "./primitives";
import { SnapshotTokens } from "./primitives";
import { wosHealth } from "./types";

const healthColor = (
  t: SnapshotTokens,
  health: ReturnType<typeof wosHealth>
): { c: string; bg: string } => {
  if (health === "under") {
    return { c: t.redInk, bg: t.red1 };
  }
  if (health === "light" || health === "heavy") {
    return { c: t.orangeInk, bg: t.orange1 };
  }
  if (health === "on") {
    return { c: t.greenInk, bg: t.green1 };
  }
  return { c: t.ink5, bg: t.surface3 };
};

const orDash = (v: string | null): string => v ?? EM_DASH;
const unOr = (v: number | null): string => (v == null ? EM_DASH : un(v));
const usdOr = (v: number | null, digits = 1): string =>
  v == null ? EM_DASH : usd(v, digits);
const clip = (s: string, max: number): string =>
  s.length > max ? s.slice(0, max - 1).trimEnd() + "…" : s;

/**
 * Order both columns so transfer lanes are short and cross as little as
 * possible: spokes sort by the units-weighted position of the hubs that
 * feed them (lane-less spokes stay with the primary hub at the top), then
 * hubs re-sort by the weighted position of the spokes they feed.
 */
const orderForShortLanes = (
  hubsIn: SnapshotLocation[],
  spokesIn: SnapshotLocation[],
  lanes: SnapshotTransferLane[] | null
): { hubs: SnapshotLocation[]; spokes: SnapshotLocation[] } => {
  if (!lanes?.length || hubsIn.length < 2) {
    return { hubs: hubsIn, spokes: spokesIn };
  }
  const hubIdx = new Map(hubsIn.map((h, i) => [h.id, i]));
  const weightedScore = (
    laneList: SnapshotTransferLane[],
    positionOf: (lane: SnapshotTransferLane) => number | undefined
  ): number | null => {
    let weighted = 0;
    let weight = 0;
    for (const lane of laneList) {
      const position = positionOf(lane);
      if (position != null) {
        weighted += position * lane.units;
        weight += lane.units;
      }
    }
    return weight > 0 ? weighted / weight : null;
  };
  const spokes = spokesIn
    .map((s, i) => ({
      s,
      i,
      score:
        weightedScore(
          lanes.filter((lane) => lane.toLocationId === s.id),
          (lane) => hubIdx.get(lane.fromHubId)
        ) ?? 0,
    }))
    .sort((a, b) => a.score - b.score || a.i - b.i)
    .map((x) => x.s);
  const spokeIdx = new Map(spokes.map((s, i) => [s.id, i]));
  const neutralSpan =
    hubsIn.length > 1 ? (spokes.length - 1) / (hubsIn.length - 1) : 0;
  const hubs = hubsIn
    .map((h, i) => ({
      h,
      i,
      score:
        weightedScore(
          lanes.filter((lane) => lane.fromHubId === h.id),
          (lane) => spokeIdx.get(lane.toLocationId)
        ) ?? i * neutralSpan,
    }))
    .sort((a, b) => a.score - b.score || a.i - b.i)
    .map((x) => x.h);
  return { hubs, spokes };
};

export const NetworkMapHubSpoke = ({
  hubs: hubsIn,
  spokes: spokesIn,
  lanes,
  inbound,
  sales,
  channels,
  period,
}: {
  hubs: SnapshotLocation[];
  spokes: SnapshotLocation[];
  lanes: SnapshotTransferLane[] | null;
  inbound: SnapshotInbound;
  sales: SnapshotSales;
  channels: SnapshotChannel[];
  period: SnapshotPeriod;
}) => {
  const t = useSnapshotTokens();
  const { hubs, spokes } = orderForShortLanes(hubsIn, spokesIn, lanes);
  const W = 1030;
  const nS = Math.max(spokes.length, 1);
  const nH = Math.max(hubs.length, 1);
  // The four-spoke artboard uses H=470 with rows at 72..402; generalize to
  // any hub/spoke count while keeping that density.
  const isMonth = period === "month";
  const supplyUnit = SUPPLY_UNIT_LABEL[period];
  // The hub also ships straight to customers. Rather than routing a band
  // around the diagram — which crossed other nodes whichever way it went —
  // that flow gets its own row in the spoke column, drawn as a pass-through
  // rather than a stocking location.
  const directOut = hubsIn[0]?.weeklyOut ?? null;
  const hasDirectRow = !!directOut;
  const rowCount = Math.max(nS + (hasDirectRow ? 1 : 0), 1);
  const H = Math.max(
    470,
    72 + (rowCount - 1) * 110 + 68,
    nH * 150 + 120,
    salesNodeHeight(channels.length) + 60
  );
  const contentCenter = H / 2;
  const hubX = 268;
  const inbPos = { x: 62, y: contentCenter };
  const spokeX = 512;
  const salesX = 826;
  const rowY = (i: number) =>
    rowCount === 1 ? contentCenter : 72 + ((H - 140) / (rowCount - 1)) * i;
  const ys = spokes.map((_, i) => rowY(i));
  // The direct row sits last, below the spokes it is drawn beside.
  const directY = hasDirectRow ? rowY(rowCount - 1) : null;
  const hubYs = hubs.map(
    (_, i) => contentCenter + (i - (hubs.length - 1) / 2) * 150
  );
  // The primary hub is the largest by units (hubsIn arrives units-sorted),
  // wherever the lane layout placed it vertically.
  const primaryHub = hubsIn[0] ?? null;
  const primaryHubY = primaryHub
    ? hubYs[hubs.indexOf(primaryHub)] ?? contentCenter
    : contentCenter;

  // One drawn lane per open hub→spoke flow; spokes with no flow keep a thin
  // unlabeled lane from the primary hub so the topology still reads. While
  // lane data hasn't loaded (`lanes` null), those default lanes carry an
  // em-dash label instead.
  const hubYById = new Map(hubs.map((h, i) => [h.id, hubYs[i]]));
  const drawnLanes = spokes.flatMap((s, i) => {
    const dataLanes = (lanes ?? []).filter(
      (lane) => lane.toLocationId === s.id
    );
    if (!dataLanes.length) {
      return [
        {
          key: s.id + "-default",
          fromY: primaryHubY,
          toY: ys[i],
          units: null as number | null,
          eta: null as string | null,
          spokeName: s.name,
          fromName: primaryHub?.name ?? "hub",
          labeled: lanes == null,
        },
      ];
    }
    return dataLanes.map((lane) => ({
      key: lane.fromHubId + "→" + s.id,
      fromY: hubYById.get(lane.fromHubId) ?? primaryHubY,
      toY: ys[i],
      units: lane.units as number | null,
      eta: lane.eta,
      spokeName: s.name,
      fromName: lane.fromHubId,
      labeled: true,
    }));
  });
  const maxLane = Math.max(1, ...drawnLanes.map((lane) => lane.units ?? 0));
  const maxOut = Math.max(
    1,
    ...hubs.map((h) => h.weeklyOut ?? 0),
    ...spokes.map((s) => s.weeklyOut ?? 0)
  );
  const laneMidX = (hubX + spokeX) / 2;

  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      style={{
        display: "block",
        width: "100%",
        maxWidth: W,
        height: "auto",
        overflow: "visible",
        fontFamily: "inherit",
      }}
    >
      <defs>
        {spokes.map((s, i) => (
          <clipPath key={s.id} id={`hs-spoke-clip-${i}`}>
            <rect
              x={spokeX - 40}
              y={ys[i] - 38}
              width="212"
              height="76"
              rx="10"
            />
          </clipPath>
        ))}
      </defs>
      {/* ── layer 1: all edges ─────────────────────────────────── */}
      {hubs.map((h, i) => (
        <g key={h.id + "-inbound"}>
          <path
            d={`M${inbPos.x + 42} ${inbPos.y} L${hubX - 66} ${hubYs[i]}`}
            stroke={t.ink5}
            strokeWidth="2"
            strokeDasharray="5 4"
          >
            <title>
              {inbound.units == null
                ? "Inbound PO units unknown"
                : `${un(inbound.units)} units inbound from vendors`}
            </title>
          </path>
          <path
            d={`M${hubX - 74} ${hubYs[i] - 6} l8 6 -8 6z`}
            fill={t.ink5}
          />
        </g>
      ))}
      {drawnLanes.map((lane) => (
        <path
          key={lane.key}
          d={`M${hubX + 62} ${lane.fromY} C ${laneMidX} ${lane.fromY}, ${laneMidX} ${lane.toY}, ${spokeX - 40} ${lane.toY}`}
          stroke={t.blue}
          strokeWidth={1.2 + ((lane.units ?? 0) / maxLane) * 3.4}
          fill="none"
          opacity="0.42"
          strokeLinecap="round"
        >
          <title>
            {lane.units == null
              ? `No open transfers headed to ${lane.spokeName}`
              : `${un(lane.units)} units in open transfers ${lane.fromName} → ${lane.spokeName}`}
          </title>
        </path>
      ))}
      {spokes.map((s, i) => (
        <path
          key={s.id + "-arrow"}
          d={`M${spokeX - 46} ${ys[i] - 5} l7 5 -7 5z`}
          fill={t.blue}
          opacity="0.6"
        />
      ))}
      {/* Hub → direct-sales row, then that row → Sales. Ordinary bands, so
          nothing has to cross a node. */}
      {primaryHub && directY != null && (
        <>
          <path
            d={`M${hubX + 62} ${primaryHubY} C ${laneMidX} ${primaryHubY}, ${laneMidX} ${directY}, ${spokeX - 40} ${directY}`}
            stroke={t.greenInk}
            strokeWidth={2 + ((directOut ?? 0) / maxOut) * 12}
            fill="none"
            opacity="0.26"
            strokeLinecap="round"
          >
            <title>
              {`${un(directOut ?? 0)} units shipped direct from ${primaryHub.name}`}
            </title>
          </path>
          <path
            d={`M${spokeX + 176} ${directY} C ${(spokeX + 176 + salesX - 6) / 2} ${directY}, ${(spokeX + 176 + salesX - 6) / 2} ${contentCenter}, ${salesX - 6} ${contentCenter}`}
            stroke={t.greenInk}
            strokeWidth={2 + ((directOut ?? 0) / maxOut) * 12}
            fill="none"
            opacity="0.26"
            strokeLinecap="round"
          />
        </>
      )}
      {spokes.map((s, i) => {
        const y = ys[i];
        const x0 = spokeX + 176;
        const x1 = salesX - 6;
        return (
          <path
            key={s.id + "-outband"}
            d={`M${x0} ${y} C ${(x0 + x1) / 2} ${y}, ${(x0 + x1) / 2} ${contentCenter}, ${x1} ${contentCenter}`}
            stroke={t.greenInk}
            // A location shipping nothing gets a hairline, not a ribbon.
            strokeWidth={s.weeklyOut ? 2 + (s.weeklyOut / maxOut) * 12 : 1}
            fill="none"
            opacity={s.weeklyOut ? 0.26 : 0.18}
            strokeLinecap="round"
          >
            <title>
              {s.weeklyOut == null
                ? `No outflow data for ${s.name}`
                : `${un(s.weeklyOut)} units/week from ${s.name} to customers`}
            </title>
          </path>
        );
      })}

      {/* ── layer 2: all edge labels, haloed ───────────────────── */}
      <g
        style={{ paintOrder: "stroke" }}
        stroke={t.surface}
        strokeWidth={3}
        strokeLinejoin="round"
      >
        <text
          x={(inbPos.x + hubX) / 2}
          y={inbPos.y - 14}
          fontSize="11"
          fontWeight="700"
          fill={t.ink2}
          textAnchor="middle"
        >
          {unOr(inbound.units)} u
        </text>
        {inbound.shipments != null && (
          <text
            x={(inbPos.x + hubX) / 2}
            y={inbPos.y + 20}
            fontSize="10"
            fill={t.ink4}
            textAnchor="middle"
          >
            {inbound.shipments} shipments
          </text>
        )}
        {drawnLanes
          .filter((lane) => lane.labeled)
          .map((lane, laneIdx) => {
            const midY = (lane.fromY + lane.toY) / 2;
            // Stagger label columns so lanes crossing the same midpoint
            // don't stack their captions.
            const labelX = laneMidX + 6 + (laneIdx % 2 === 0 ? -32 : 32);
            return (
              <g key={lane.key + "-lbl"}>
                <text
                  x={labelX}
                  y={midY - 4}
                  fontSize="10.5"
                  fontWeight="700"
                  fill={t.blueDeep}
                  textAnchor="middle"
                >
                  {unOr(lane.units)} u
                </text>
                {lane.eta && (
                  <text
                    x={labelX}
                    y={midY + 10}
                    fontSize="9.5"
                    fill={t.ink4}
                    textAnchor="middle"
                  >
                    ETA {lane.eta}
                  </text>
                )}
              </g>
            );
          })}
        {spokes.map((s, i) => {
          const y = ys[i];
          const x0 = spokeX + 176;
          return (
            <g key={s.id + "-outlbl"}>
              <text
                x={x0 + 30}
                y={y - 9}
                fontSize="10"
                fontWeight="700"
                fill={t.greenInk}
              >
                {s.weeklyOut == null
                  ? EM_DASH
                  : un(s.weeklyOut) + (isMonth ? "/mo" : "/wk")}
              </text>
              {s.weeklyOut != null && s.units != null && s.units > 0 && (
                <text x={x0 + 30} y={y + 5} fontSize="9" fill={t.ink4}>
                  {isMonth
                    ? `${(s.weeklyOut / s.units).toFixed(2)}× stock`
                    : `${Math.round((s.weeklyOut / s.units) * 100)}% of stock`}
                </text>
              )}
            </g>
          );
        })}
        {directY != null && (
          <g>
            <text
              x={laneMidX + 6}
              y={directY - 8}
              fontSize="10.5"
              fontWeight="700"
              fill={t.greenInk}
              textAnchor="middle"
            >
              {un(directOut ?? 0)} u
            </text>
            <text
              x={laneMidX + 6}
              y={directY + 6}
              fontSize="9.5"
              fill={t.ink4}
              textAnchor="middle"
            >
              direct
            </text>
          </g>
        )}
      </g>

      {/* column captions */}
      <text
        x={hubX}
        y={20}
        fontSize="9.5"
        fontWeight="700"
        letterSpacing="0.7"
        fill={t.ink4}
        textAnchor="middle"
      >
        INFLOW → STOCK
      </text>
      <text
        x={(spokeX + salesX) / 2 + 60}
        y={20}
        fontSize="9.5"
        fontWeight="700"
        letterSpacing="0.7"
        fill={t.greenInk}
        textAnchor="middle"
      >
        OUTFLOW → SALES
      </text>

      <SalesNode
        t={t}
        x={salesX}
        centerY={contentCenter}
        sales={sales}
        channels={channels}
        period={period}
      />

      {/* inbound node */}
      <g>
        <rect
          x={inbPos.x - 42}
          y={inbPos.y - 30}
          width="84"
          height="60"
          rx="9"
          fill={t.surface3}
          stroke={t.line}
        >
          <title>Vendor POs inbound to the hub network</title>
        </rect>
        <text
          x={inbPos.x}
          y={inbPos.y - 10}
          fontSize="10"
          fill={t.ink4}
          textAnchor="middle"
        >
          Inbound
        </text>
        <text
          x={inbPos.x}
          y={inbPos.y + 8}
          fontSize="15"
          fontWeight="700"
          fill={t.ink}
          textAnchor="middle"
        >
          {unOr(inbound.units)}
        </text>
        <text
          x={inbPos.x}
          y={inbPos.y + 22}
          fontSize="9.5"
          fill={t.ink4}
          textAnchor="middle"
        >
          {usdOr(inbound.value)}
        </text>
      </g>

      {/* hub nodes — one ring per hub-role location */}
      {hubs.map((h, i) => {
        const cy = hubYs[i];
        const health = healthColor(t, wosHealth(h.wos, h.targetWos));
        return (
          <g key={h.id}>
            <circle
              cx={hubX}
              cy={cy}
              r="62"
              fill={t.surface}
              stroke={health.c}
              strokeWidth="3"
            >
              <title>
                {h.units == null
                  ? `${h.name} — on-hand units unknown`
                  : `${h.name} — ${un(h.units)} units on hand`}
              </title>
            </circle>
            <circle
              cx={hubX}
              cy={cy}
              r="62"
              fill={health.bg}
              opacity="0.45"
              style={{ pointerEvents: "none" }}
            />
            <text
              x={hubX}
              y={cy - 26}
              fontSize="9.5"
              fontWeight="700"
              letterSpacing="0.6"
              fill={t.blueDeep}
              textAnchor="middle"
            >
              HUB
            </text>
            <text
              x={hubX}
              y={cy - 8}
              fontSize="12"
              fontWeight="600"
              fill={t.ink}
              textAnchor="middle"
            >
              {clip(h.short, 16)}
            </text>
            <text
              x={hubX}
              y={cy + 14}
              fontSize="20"
              fontWeight="700"
              fill={t.ink}
              textAnchor="middle"
            >
              {unOr(h.units)}
            </text>
            <text
              x={hubX}
              y={cy + 30}
              fontSize="10.5"
              fontWeight="600"
              fill={health.c}
              textAnchor="middle"
            >
              {h.displayWos == null || h.displayTargetWos == null
                ? EM_DASH
                : `${h.displayWos.toFixed(1)}${supplyUnit} / ${h.displayTargetWos.toFixed(1)}`}
            </text>
            <text
              x={hubX}
              y={cy + 46}
              fontSize="10"
              fill={t.ink4}
              textAnchor="middle"
            >
              {usdOr(h.value)}
            </text>
          </g>
        );
      })}

      {/* Direct-sales row: a pass-through, not a stocking location, so it
          is dashed and carries no on-hand, WOS or at-risk figures. */}
      {primaryHub && directY != null && (
        <g>
          <rect
            x={spokeX - 40}
            y={directY - 30}
            width="212"
            height="60"
            rx="10"
            fill={t.surface3}
            stroke={t.ink5}
            strokeWidth="1.4"
            strokeDasharray="5 4"
          >
            <title>
              {`Stock shipped straight from ${primaryHub.name} to customers, bypassing the spokes`}
            </title>
          </rect>
          <text
            x={spokeX - 26}
            y={directY - 10}
            fontSize="11.5"
            fontWeight="700"
            fill={t.ink2}
          >
            Direct B2B sales
          </text>
          <text x={spokeX - 26} y={directY + 10} fontSize="15" fontWeight="700" fill={t.ink}>
            {un(directOut ?? 0)}
            <tspan fontSize="10" fontWeight="500" fill={t.ink3}>
              {isMonth ? " u/mo" : " u/wk"}
            </tspan>
          </text>
          <text
            x={spokeX + 158}
            y={directY + 10}
            fontSize="9.5"
            fill={t.ink4}
            textAnchor="end"
          >
            {primaryHub.units
              ? isMonth
                ? `${((directOut ?? 0) / primaryHub.units).toFixed(2)}× hub stock`
                : `${Math.round(((directOut ?? 0) / primaryHub.units) * 100)}% of hub stock`
              : ""}
          </text>
          <text
            x={spokeX + 158}
            y={directY - 10}
            fontSize="9"
            fill={t.ink4}
            textAnchor="end"
          >
            from {clip(primaryHub.short, 14)}
          </text>
        </g>
      )}

      {/* spoke nodes */}
      {spokes.map((s, i) => {
        const y = ys[i];
        const h = healthColor(t, wosHealth(s.wos, s.targetWos));
        return (
          <g key={s.id}>
            <rect
              x={spokeX - 40}
              y={y - 38}
              width="212"
              height="76"
              rx="10"
              fill={t.surface}
              stroke={h.c}
              strokeWidth="1.6"
            >
              <title>
                {s.units == null
                  ? `${s.name} — on-hand units unknown`
                  : `${s.name} — ${un(s.units)} units on hand`}
              </title>
            </rect>
            {/* Health bar, clipped to the node's rounded outline so it
                follows the corner radius instead of overlapping it. */}
            <rect
              x={spokeX - 40}
              y={y - 38}
              width="4"
              height="76"
              fill={h.c}
              clipPath={`url(#hs-spoke-clip-${i})`}
            />
            <text
              x={spokeX - 26}
              y={y - 20}
              fontSize="12"
              fontWeight="700"
              fill={t.ink}
            >
              {clip(s.name, 26)}
            </text>
            {s.id !== s.name && (
              <text
                x={spokeX + 158}
                y={y - 20}
                fontSize="9.5"
                fill={t.ink4}
                textAnchor="end"
              >
                {s.id}
              </text>
            )}
            <text
              x={spokeX - 26}
              y={y + 4}
              fontSize="17"
              fontWeight="700"
              fill={t.ink}
            >
              {unOr(s.units)}
            </text>
            <text
              x={spokeX + 158}
              y={y + 4}
              fontSize="13"
              fontWeight="700"
              fill={h.c}
              textAnchor="end"
            >
              {s.displayWos == null
                ? EM_DASH
                : s.displayWos.toFixed(1) + supplyUnit}
            </text>
            <text x={spokeX - 26} y={y + 22} fontSize="9.5" fill={t.ink4}>
              {usdOr(s.value)}
              {s.inStock != null ? ` · ${s.inStock.toFixed(1)}%` : ""}
            </text>
            <text
              x={spokeX + 158}
              y={y + 22}
              fontSize="9.5"
              fill={s.atRisk != null && s.atRisk > 15 ? t.redInk : t.ink4}
              textAnchor="end"
              fontWeight={s.atRisk != null && s.atRisk > 15 ? 700 : 400}
            >
              {orDash(s.atRisk == null ? null : `${s.atRisk} SKUs at risk`)}
            </text>
          </g>
        );
      })}
    </svg>
  );
};
